import { Dialog, MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { Button, buttonStylesPrimary, comboboxLoader, Loader, LoaderType } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import type { Workspace } from '../../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { usePermissions } from '../../../../authz/providers/PermissionsProvider/PermissionsProvider';
import type { S3Object } from '../../../../drive/types';
import { DriveUserActionType } from '../../../../drive/userActions';
import { useWorkspaces } from '../../../Orchestrator/WorkspaceProvider';
import { createDirectoryInWorkspace, listObjectsFromWorkspace } from '../../helpers/crossWorkspaceS3';
import { DirectoryNameModal } from '../DirectoryNameModal';
import { ModalContent } from './ModalContent';
import { getDirectoriesForPath } from './utils';
import { getSourceDirectory, validateCopyMoveOperation } from './validation';

export interface DestinationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (destinationPath: string, destinationWorkspace?: Workspace) => Promise<void>;
  allObjects: S3Object[] | undefined;
  sourceItemName: string;
  sourceKey: string;
  operation: 'copy' | 'move';
  s3Service: {
    createDirectory: (name: string, path: string) => Promise<void>;
    listAllObjects: (workspaceId?: string) => Promise<S3Object[]>;
  };
  onObjectsRefresh: () => void;
  stsDriveUrl?: string;
  workspaceDriveUrl?: string;
  isOperationInProgress?: boolean;
}

export const DestinationSelectionModal = (props: DestinationSelectionModalProps) => {
  const {
    isOpen,
    onClose,
    onConfirm,
    allObjects,
    sourceItemName,
    sourceKey,
    operation,
    s3Service,
    onObjectsRefresh,
    stsDriveUrl,
    workspaceDriveUrl,
    isOperationInProgress = false,
  } = props;
  const { activeWorkspace } = useWorkspaces();

  const [modalCurrentPath, setModalCurrentPath] = useState<string>('');
  const [modalObjects, setModalObjects] = useState<S3Object[]>([]);
  const [showCreateDirectoryModal, setShowCreateDirectoryModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | undefined>(activeWorkspace);
  const [workspaceObjects, setWorkspaceObjects] = useState<S3Object[] | undefined>(allObjects);
  const [isLoadingWorkspaceObjects, setIsLoadingWorkspaceObjects] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(0);
  const [canWriteObjects] = usePermissions([DriveUserActionType.WRITE_OBJECTS], selectedWorkspace?.name);

  const isCrossWorkspace = selectedWorkspace?.name !== activeWorkspace?.name;

  // Validate copy/move operation
  const validation = validateCopyMoveOperation(
    sourceKey,
    modalCurrentPath,
    sourceItemName,
    workspaceObjects,
    isCrossWorkspace,
    canWriteObjects
  );

  const { isDestinationSameAsSource, hasConflict, hasPermissionError } = validation;

  const handleNavigateToDirectory = useCallback(
    (directoryPath: string) => {
      if (!workspaceObjects) return;

      setModalCurrentPath(directoryPath);
      const items = getDirectoriesForPath(workspaceObjects, directoryPath);
      setModalObjects(items);
    },
    [workspaceObjects]
  );

  const handleWorkspaceChange = useCallback(
    async (workspace: Workspace | undefined) => {
      if (!workspace) return;

      const requestId = currentRequestId + 1;
      setCurrentRequestId(requestId);
      setSelectedWorkspace(workspace);
      setModalCurrentPath('');

      if (workspace.name === activeWorkspace?.name) {
        setWorkspaceObjects(allObjects);
        const items = getDirectoriesForPath(allObjects, '');
        setModalObjects(items);
        return;
      }

      if (!stsDriveUrl || !workspaceDriveUrl) {
        console.error('Drive URLs not available for cross-workspace operation');
        return;
      }

      try {
        setIsLoadingWorkspaceObjects(true);
        const objects = await listObjectsFromWorkspace({
          workspaceId: workspace.name || '',
          stsDriveUrl,
          workspaceDriveUrl,
        });

        // Only update state if this is still the latest request
        if (requestId === currentRequestId + 1) {
          setWorkspaceObjects(objects);
          const items = getDirectoriesForPath(objects, '');
          setModalObjects(items);
        }
      } catch (error) {
        // Only update state if this is still the latest request
        if (requestId === currentRequestId + 1) {
          console.error('Failed to fetch objects from workspace:', error);
          setWorkspaceObjects([]);
          setModalObjects([]);
        }
      } finally {
        // Only update loading state if this is still the latest request
        if (requestId === currentRequestId + 1) {
          setIsLoadingWorkspaceObjects(false);
        }
      }
    },
    [activeWorkspace, allObjects, stsDriveUrl, workspaceDriveUrl, currentRequestId]
  );

  useEffect(() => {
    if (isOpen) {
      // Initialize modal state - navigate to source file's directory
      setSelectedWorkspace(activeWorkspace);
      setWorkspaceObjects(allObjects);
      const sourceDir = getSourceDirectory(sourceKey);
      setModalCurrentPath(sourceDir);
      const items = getDirectoriesForPath(allObjects, sourceDir);
      setModalObjects(items);
    } else {
      // Reset state on close
      setSelectedWorkspace(activeWorkspace);
      setWorkspaceObjects(undefined);
      setModalCurrentPath('');
      setModalObjects([]);
      setIsLoadingWorkspaceObjects(false);
      setCurrentRequestId(0);
    }
  }, [isOpen, sourceKey]);

  useEffect(() => {
    if (isOpen && allObjects && allObjects.length > 0) {
      const items = getDirectoriesForPath(allObjects, modalCurrentPath);
      setModalObjects(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allObjects]);

  const handleConfirm = () => {
    onConfirm(modalCurrentPath, selectedWorkspace);
  };

  const handleCreateDirectory = useCallback(
    async (directoryName: string) => {
      if (!selectedWorkspace) return;

      const isCrossWorkspace = selectedWorkspace.name !== activeWorkspace?.name;

      try {
        if (isCrossWorkspace) {
          if (!stsDriveUrl || !workspaceDriveUrl) {
            console.error('Drive URLs not available for cross-workspace operation');
            return;
          }

          const config = {
            workspaceId: selectedWorkspace.name || '',
            stsDriveUrl,
            workspaceDriveUrl,
          };

          await createDirectoryInWorkspace(config, directoryName, modalCurrentPath);
          setShowCreateDirectoryModal(false);

          // Re-fetch and stay in current path
          setIsLoadingWorkspaceObjects(true);
          const objects = await listObjectsFromWorkspace(config);
          setWorkspaceObjects(objects);
          const items = getDirectoriesForPath(objects, modalCurrentPath);
          setModalObjects(items);
          setIsLoadingWorkspaceObjects(false);
        } else {
          // Same workspace - use existing connection
          await s3Service.createDirectory(directoryName, modalCurrentPath);
          setShowCreateDirectoryModal(false);

          onObjectsRefresh();
          handleNavigateToDirectory(modalCurrentPath);
        }
      } catch (error) {
        console.error('Failed to create directory:', error);
        setIsLoadingWorkspaceObjects(false);
      }
    },
    [
      selectedWorkspace,
      activeWorkspace,
      stsDriveUrl,
      workspaceDriveUrl,
      modalCurrentPath,
      s3Service,
      onObjectsRefresh,
      handleNavigateToDirectory,
    ]
  );

  const getTitle = () => {
    return `${operation === 'copy' ? 'Copy' : 'Move'} "${sourceItemName}"`;
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onClose}
      minWidth={600}
      dialogContentProps={{
        type: 0,
        title: getTitle(),
        closeButtonAriaLabel: 'Close',
        showCloseButton: !isOperationInProgress,
      }}
      modalProps={{ isBlocking: isOperationInProgress }}
    >
      {isLoadingWorkspaceObjects ? (
        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: 32, minHeight: 400 } }}>
          <div>Loading files from {selectedWorkspace?.displayName || 'workspace'}...</div>
        </Stack>
      ) : (
        <ModalContent
          currentPath={modalCurrentPath}
          objects={modalObjects}
          onNavigate={handleNavigateToDirectory}
          selectedWorkspace={selectedWorkspace}
          onWorkspaceChange={handleWorkspaceChange}
          isCrossWorkspace={isCrossWorkspace}
          operation={operation}
          sourceWorkspace={activeWorkspace}
        />
      )}

      {hasConflict && (
        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: 16 } }}>
          A file or directory named &quot;{sourceItemName}&quot; already exists at this location.
        </MessageBar>
      )}

      <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} hidden={!isOperationInProgress} />

      <Stack
        horizontal
        tokens={{ childrenGap: 8 }}
        horizontalAlign="space-between"
        styles={{ root: { width: '100%', paddingTop: isOperationInProgress ? 8 : 16 } }}
      >
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Button
            onClick={() => setShowCreateDirectoryModal(true)}
            iconName="NewFolder"
            text="Create directory"
            data-test="create-directory-button"
            disabled={isLoadingWorkspaceObjects || hasPermissionError || isOperationInProgress}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Button text="Cancel" onClick={onClose} data-test="cancel-button" disabled={isOperationInProgress} />
          <Button
            styles={buttonStylesPrimary}
            text={operation === 'copy' ? 'Copy' : 'Move'}
            onClick={handleConfirm}
            disabled={
              isDestinationSameAsSource ||
              isLoadingWorkspaceObjects ||
              hasPermissionError ||
              isOperationInProgress ||
              hasConflict
            }
            data-test={`${operation}-button`}
          />
        </Stack>
      </Stack>

      <DirectoryNameModal
        isOpen={showCreateDirectoryModal}
        onClose={() => setShowCreateDirectoryModal(false)}
        onConfirm={handleCreateDirectory}
        existingDirectories={modalObjects.map((obj) => obj.Key!).filter(Boolean)}
        title="Create New Directory"
        confirmButtonText="Create"
      />
    </Dialog>
  );
};
